import React, { useState, useEffect, useCallback } from "react";
import inventoryApi from "./inventoryApi";
import ExtraUsageModal from "./ExtraUsageModal";

export default function InventoryUsageModal({
  campId,
  technicianId,
  onClose,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("consumable");
  const [selectedItem, setSelectedItem] = useState(null);

  const [isConfirmed, setIsConfirmed] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await inventoryApi.fetchCampTechnicianUsage(
        campId,
        technicianId
      );

      console.log("API RESULT:", result);
      setData(result);
    } catch (err) {
      console.error("API ERROR:", err);
      setError("Failed to load inventory usage.");
    } finally {
      setLoading(false);
    }
  }, [campId, technicianId]);

  const checkConfirmation = useCallback(async () => {
    try {
      const result = await inventoryApi.getTodayConfirmation(
        technicianId,
        campId
      );

      console.log("Confirmation Result:", result);

      setAlreadySubmitted(
        result?.submitted ||
          result?.already_submitted ||
          result?.is_confirmed ||
          false
      );
    } catch (error) {
      console.error(error);
    }
  }, [technicianId, campId]);

  useEffect(() => {
    loadData();
    checkConfirmation();
  }, [loadData, checkConfirmation]);

  const getItemsByType = (type) => {
    if (!data?.all_time_items) return [];

    if (type === "consumable") {
      return data.all_time_items.filter(
        (item) =>
          !item.inventory_type ||
          item.inventory_type === "consumable"
      );
    }

    return data.all_time_items.filter(
      (item) => item.inventory_type === "capital"
    );
  };

  const getTodayUsage = (itemId) => {
    const todayItem = data?.today?.items?.find(
      (i) => i.inventory_item_id === itemId
    );

    return todayItem?.quantity_used || 0;
  };

  const handleConfirmationSubmit = async () => {
    if (!isConfirmed) {
      alert("Please confirm inventory usage first.");
      return;
    }

    try {
      setSubmitting(true);

      await inventoryApi.submitDailyUsageConfirmation({
        technician_id: technicianId,
        camp_id: campId,
        is_confirmed: isConfirmed,
        remarks,
      });

      alert("Confirmation submitted successfully");

      setAlreadySubmitted(true);

      onClose();
    } catch (err) {
      console.error(err);
      alert("Failed to submit confirmation");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-bold text-gray-900">
            Camp Inventory Usage
          </h2>

          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
          >
            ×
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b px-6">
          {["consumable", "capital"].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className="px-6 py-3 text-sm font-medium capitalize transition-colors"
              style={{
                borderBottom:
                  activeTab === tab
                    ? "3px solid #11a8a4"
                    : "3px solid transparent",
                color:
                  activeTab === tab
                    ? "#11a8a4"
                    : "#6b7280",
              }}
            >
              {tab === "consumable"
                ? "Consumables"
                : "Capital Items"}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 p-6">
          {loading && (
            <div className="text-center py-12">
              <div
                className="animate-spin rounded-full h-10 w-10 border-b-2 mx-auto mb-3"
                style={{ borderColor: "#11a8a4" }}
              />
              <p className="text-gray-500">
                Loading inventory data...
              </p>
            </div>
          )}

          {error && (
            <p className="text-red-600 text-center py-8">
              {error}
            </p>
          )}

          {!loading && !error && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {getItemsByType(activeTab).length === 0 && (
                <p className="text-gray-400 col-span-2 text-center py-8">
                  No {activeTab} items found.
                </p>
              )}

              {getItemsByType(activeTab).map((item) => (
                <div
                  key={item.inventory_item_id}
                  className="bg-gray-50 rounded-lg p-4 border border-gray-200 shadow-sm"
                >
                  <p className="font-semibold text-gray-900">
                    {item.inventory_item__item_name}
                  </p>

                  <p className="text-sm text-gray-500 mb-3">
                    Code: {item.inventory_item__item_code}
                  </p>

                  <div className="flex justify-between text-sm mb-3">
                    <span className="text-gray-600">
                      Today's Usage:
                      <strong>
                        {" "}
                        {getTodayUsage(
                          item.inventory_item_id
                        )}
                      </strong>
                    </span>

                    <span className="text-gray-600">
                      Total Usage:
                      <strong> {item.total_used}</strong>
                    </span>
                  </div>

                  {activeTab === "consumable" && (
                    <button
                      onClick={() =>
                        setSelectedItem({
                          ...item,
                          todayUsage: getTodayUsage(
                            item.inventory_item_id
                          ),
                        })
                      }
                      className="w-full text-white py-2 rounded-md text-sm font-medium"
                      style={{ background: "#91d537" }}
                    >
                      Log Extra Usage
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Confirmation Section */}
        <div className="border-t p-6 bg-gray-50">
          <h3 className="font-semibold text-lg mb-3">
            Daily Usage Confirmation
          </h3>

          {alreadySubmitted && (
            <div className="bg-green-100 text-green-700 p-3 rounded mb-3">
              Today's usage already confirmed.
            </div>
          )}

          <label className="flex items-center gap-2 mb-3">
            <input
              type="checkbox"
              checked={isConfirmed}
              onChange={(e) =>
                setIsConfirmed(e.target.checked)
              }
              disabled={alreadySubmitted}
            />

            <span>
              I confirm today's inventory usage count is
              correct.
            </span>
          </label>

          <textarea
            className="w-full border rounded-lg p-3 mb-3"
            rows="3"
            placeholder="Remarks (optional)"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            disabled={alreadySubmitted}
          />

          <button
            onClick={handleConfirmationSubmit}
            disabled={
              alreadySubmitted || submitting
            }
            className="bg-teal-600 text-white px-5 py-2 rounded-lg disabled:bg-gray-400"
          >
            {submitting
              ? "Submitting..."
              : alreadySubmitted
              ? "Already Submitted"
              : "Submit Confirmation"}
          </button>
        </div>
      </div>

      {/* Extra Usage Modal */}
      {selectedItem && (
        <ExtraUsageModal
          campId={campId}
          technicianId={technicianId}
          item={selectedItem}
          onClose={() => setSelectedItem(null)}
          onSuccess={() => {
            setSelectedItem(null);
            loadData();
          }}
        />
      )}
    </div>
  );
}